import React, { useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'absolute',
    zIndex: 100000,
    background: 'white',
    padding: '5px 5px',
    boxShadow: '0px 4px 8px #0000000f',
    borderRadius: 8,
    border: '1px solid #E0E3E7'
  },
  noPaddingContainer: {
    extend: 'container',
    padding: 0
  },
  modalContainer: {
    extend: 'container',
    padding: 0,
    border: 'none',
    borderRadius: 13,
    top: 100,
    left: '50%',
    transform: 'translate(-50%, 0%)'
  },
  backgroundHider: {
    width: '100vw',
    height: '100vh',
    position: 'absolute',
    zIndex: 10000
  },
  modalBackgroundHider: {
    extend: 'backgroundHider',
    position: 'fixed',
    background: 'rgba(0,0,0,0.6)',
    overflowY: 'scroll'
  },
  menuItem: {
    padding: '8px 20px',
    cursor: 'pointer',
    width: 153,
    borderRadius: 5,
    fontSize: 14,
    display: 'grid',
    gridTemplateColumns: '28px 1fr',
    '&:hover': {
      background: '#f7f7f8'
    }
  }
}));

export type Option = {
  name?: string;
  icon?: IconProp;
  onClick?: () => void;
};

export type Placement =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'modal';

export type Position = {
  x: number;
  y: number;
  placement?: Placement;
};

type Props = {
  options?: Option[];
  position?: Position;
  children?: any;
  open: boolean;
  onClose?: () => void;
};

export default function ActionMenu({
  options,
  position,
  open,
  children,
  onClose
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const containerRef = useRef<any>(null);
  const [_position, setPosition] = React.useState<Position | null>(
    position ?? null
  );

  let placement = 'top-right';
  if (position?.placement) {
    placement = position.placement;
  }

  useEffect(() => {
    if (!open || !position) return;

    let top = position.y;
    let left = position.x;
    if (placement && containerRef.current) {
      let size = containerRef.current.getBoundingClientRect();

      switch (placement) {
        case 'top-left':
          top = position.y - size.height;
          left = position.x - size.width;
          break;
        case 'top-right':
          top = position.y - size.height;
          left = position.x;
          break;
        case 'bottom-right':
          top = position.y;
          left = position.x;
          break;
        case 'bottom-left':
          top = position.y;
          left = position.x - size.width;
          break;
      }
      setPosition({
        x: left,
        y: top
      });
    }
  }, [position, open, options]);

  if (!open) return null;

  let style = {};

  let containerClass = classes.container;
  let hiderClass = classes.backgroundHider;
  if (children) {
    containerClass = classes.noPaddingContainer;
  }

  if (placement == 'modal') {
    containerClass = classes.modalContainer;
    hiderClass = classes.modalBackgroundHider;
  } else {
    style = _position
      ? { top: _position.y, left: _position.x }
      : { opacity: 0 };
  }

  return (
    <div
      className={hiderClass}
      onClick={(evt) => {
        evt.stopPropagation();
        setPosition(null);
        onClose && onClose();
      }}
    >
      <div className={containerClass} ref={containerRef} style={style}>
        {children}
        {options?.map((option, index) => (
          <div
            className={classes.menuItem}
            onClick={(evt) => {
              evt.stopPropagation();
              option.onClick && option.onClick();
              setPosition(null);
              onClose && onClose();
            }}
          >
            <div>{option.icon && <FontAwesomeIcon icon={option.icon} />}</div>
            <div>{option.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
